import { cloneDeep } from 'lodash';
import { IPoint, IPositionsAndTotalHeight, ITextAttributes, ITextPartRenderingAttributes, ITextPartAttributes, ITextStyle, ITextSelectionInformation, ICommonAttributes} from 'routes/private/Editor/types';
import { IHtmlTagDefinition } from 'components/HtmlTagRendering/types'
import {IFontSizeValue} from 'routes/private/Editor/Editor/CommandsBars/TextCommandBar/CommandsOptions/FontSizeCommandOption/types';
import { measureText } from 'utils/render'

interface ITextCommandAliasDefinition {
    htmlTag: string,
    attributeAffected?: string,
    styleAffected?: string,
    value?: string
}

interface ITextCommandDefinition {
    command: string,
    htmlTag: string,
    scope: string[],
    attributeAffected?: string,
    styleAffected?: string,
    value?: string,
    aliases?: ITextCommandAliasDefinition[]
}

export const superSupFactor: number = 0.5;

const textCommandDefinitions: ITextCommandDefinition[] = [
    {
        command: "fontFamily",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "font-family",
        aliases: [{
            htmlTag: "font",
            attributeAffected: "face"
        }]
    },
    {
        command: "fontSize",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "font-size"
    },
    {
        command: "fontItalic",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "font-style",
        value: "italic",
        aliases: [{
            htmlTag: "i",
        }]
    },
    {
        command: "fontUpperCase",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "text-transform",
        value: "uppercase"
    },
    {
        command: "fontWeight",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "font-weight",
        value: "bold",
        aliases: [
            {
                htmlTag: "b",
            },
            {
                htmlTag: "strong",
            }
        ]
    },
    {
        command: "color",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "color",
        aliases: [{
            htmlTag: "font",
            attributeAffected: "color"
        }]
    },
    {
        command: "fontUnderline",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "text-decoration-line",
        value: "underline",
        aliases: [{
            htmlTag: "u",
        }]
    },
    {
        command: "subscript",
        htmlTag: "sub",
        scope: ["SELECTION","ALL"],
        styleAffected: "font-size"
    },
    {
        command: "superscript",
        htmlTag: "sup",
        scope: ["SELECTION","ALL"],
        styleAffected: "font-size"
    },
    {
        command: "textAlign",
        htmlTag: "div",
        scope: ["ALL"],
        styleAffected: "text-align"
    },
    {
        command: "letterSpacing",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "letter-spacing"
    },
    {
        command: "lineHeight",
        htmlTag: "span",
        scope: ["SELECTION","ALL"],
        styleAffected: "line-height"
    }
];

const getTextCommandDefinitionByCommand = (command: string): ITextCommandDefinition|undefined => {
    for (let i=0;i<textCommandDefinitions.length;i++) {
        if (textCommandDefinitions[i].command === command) return textCommandDefinitions[i];
    }
    return undefined;
}

export interface IHtmlTagString {
    openTag: string;
    closeTag: string;
}

export const execCommandToHtmlString = (command: string, value?: string): IHtmlTagString|undefined => {
    const commandDefinition: ITextCommandDefinition|undefined = getTextCommandDefinitionByCommand(command);
    if (commandDefinition) {
        let openTag = "<" + commandDefinition.htmlTag;
        const closeTag = "</" + commandDefinition.htmlTag + ">";
        let applyValue = "";
        if (value) applyValue=value;
        else if (commandDefinition.value) applyValue=commandDefinition.value!;
        if (commandDefinition.styleAffected) {
            openTag += " style=\"" + commandDefinition.styleAffected + ":" + applyValue + "\"";
        }
        if (commandDefinition.attributeAffected) {
            openTag += " " + commandDefinition.attributeAffected + "=\"" + applyValue + "\"";
        }
        openTag += ">";
        return {openTag: openTag, closeTag: closeTag}; 
    }
    return undefined;
}

export const execCommandAtHtmlText = (command: string, htmlText: IHtmlTagDefinition, value?: string): IHtmlTagDefinition => {
    const commandDefinition: ITextCommandDefinition|undefined = getTextCommandDefinitionByCommand(command);
    if (commandDefinition) {
        let htmlElement: IHtmlTagDefinition = {tagName: commandDefinition.htmlTag};
        let valueApply = value;
        if (value===undefined && commandDefinition.value) valueApply=commandDefinition.value!;
        if (commandDefinition.styleAffected) {
            const style: any = {};
            style[commandDefinition.styleAffected!]=valueApply;
            htmlElement = {...htmlElement, properties: {style: style}};
        }
        if (commandDefinition.attributeAffected) {
            const properties: any = {};
            properties[commandDefinition.attributeAffected]=valueApply;
            htmlElement = {...htmlElement, properties: properties};
        }
        htmlElement = {...htmlElement, childrens: [htmlText]};
        return htmlElement; 
    }
    return htmlText;
}

export const getTextPart = (textParts: ITextPartAttributes[], text: string, index: number): string => {
    if (textParts[index] === undefined) return "";
    let start: number = 0;
    for (let i=0;i<index;i++) {
        start+=textParts[i].length;
    }
    const textPart = text.substring(start,start + textParts[index].length);
    return textPart;
}

const insertHtmlTagString = (htmlTagString: IHtmlTagString, htmlTagStringToInsert: IHtmlTagString|undefined): IHtmlTagString => {
    if (htmlTagStringToInsert) {
        htmlTagString.openTag += htmlTagStringToInsert.openTag; 
        htmlTagString.closeTag = htmlTagStringToInsert.closeTag + htmlTagString.closeTag; 
    }
    return htmlTagString;
}

const partToHtmlString = (partElement: any, textElement: ITextAttributes, index: number, lineHeight?: number): string => {
    let htmlTagString: IHtmlTagString = {openTag: "<span class=\"partTextElement\">", closeTag: "</span>"};
    
    if (partElement.style) {
        if (partElement.style.fontFamily) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("fontFamily", "font-" + partElement.style.fontFamily));
        if (partElement.style.fontSize) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("fontSize", partElement.style.fontSize.toString() + "px"));
        if (partElement.style.fontItalic) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("fontItalic"));
        if (partElement.style.fontUpperCase) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("fontUpperCase"));
        if (partElement.style.fontWeight) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("fontWeight"));
        if (partElement.style.color) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("color", partElement.style.color));
        if (partElement.style.fontUnderline) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("fontUnderline"));
        if (partElement.style.subscript) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("subscript",partElement.style.fontSize?(partElement.style.fontSize * superSupFactor).toString() + "px":""));
        if (partElement.style.superscript) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("superscript",partElement.style.fontSize?(partElement.style.fontSize * superSupFactor).toString() + "px":""));
        if (partElement.style.lineHeight && lineHeight === undefined) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("lineHeight",partElement.style.lineHeight.toString()));
        if (lineHeight !== undefined) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("lineHeight",lineHeight.toString()));
        if (partElement.style.letterSpacing) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("letterSpacing",((partElement.style.letterSpacing)/100).toString() + "px"));
    }

    let textPart = "";
    if (partElement.text) {
        textPart = partElement.text;
    }
    else {
        textPart = getTextPart(textElement.parts, textElement.text, index);
    }
    const html = htmlTagString.openTag + "<span>" + textPart + "</span>" + htmlTagString.closeTag;
    return html;
}

const partToHtmlTag = (partElement: ITextPartAttributes, textElement: ITextAttributes, index: number): IHtmlTagDefinition|undefined => {
    let partHtmlElement: IHtmlTagDefinition = {tagName: "span", properties: {className: "partTextElement"}};

    const textPart = getTextPart(textElement.parts, textElement.text , index);

    let textContainer: IHtmlTagDefinition = {tagName: "span", innerText: textPart};
    
    if (partElement.style) {
        if (partElement.style.superscript) {
            textContainer = execCommandAtHtmlText("superscript", textContainer, (partElement.style.fontSize?(partElement.style.fontSize * superSupFactor).toString() + "px":"")); 
        }
        if (partElement.style.subscript) {
            textContainer = execCommandAtHtmlText("subscript", textContainer, (partElement.style.fontSize?(partElement.style.fontSize * superSupFactor).toString() + "px":"")); 
        }
        if (partElement.style.fontUnderline) {
            textContainer = execCommandAtHtmlText("fontUnderline", textContainer); 
        }
        if (partElement.style.color) {
            textContainer = execCommandAtHtmlText("color", textContainer, partElement.style.color); 
        }
        if (partElement.style.fontWeight) {
            textContainer = execCommandAtHtmlText("fontWeight", textContainer); 
        }
        if (partElement.style.fontUpperCase) {
            textContainer = execCommandAtHtmlText("fontUpperCase", textContainer); 
        }
        if (partElement.style.fontItalic) {
            textContainer = execCommandAtHtmlText("fontItalic", textContainer); 
        }
        if (partElement.style.fontSize) {
            textContainer = execCommandAtHtmlText("fontSize", textContainer, partElement.style.fontSize.toString() + "px"); 
        }
        if (partElement.style.fontFamily) {
            textContainer = execCommandAtHtmlText("fontFamily", textContainer, `font-${partElement.style.fontFamily}`); 
        }
        if (partElement.style.lineHeight) {
            textContainer = execCommandAtHtmlText("lineHeight", textContainer, partElement.style.lineHeight.toString()); 
        }
        if (partElement.style.letterSpacing) {
            textContainer = execCommandAtHtmlText("letterSpacing", textContainer, ((partElement.style.letterSpacing)/100).toString() + "px"); 
        }
    }
    partHtmlElement = {...partHtmlElement, childrens: [textContainer]};
    return partHtmlElement;
}

const removeUndefinedFields = (obj: any) => {
    Object.keys(obj).forEach(key => {
        if (obj[key] === undefined) {
          delete obj[key];
        }
      });
}

export const publishGeneralAttributesToParts = (textElement: ITextAttributes): ITextPartAttributes[] => {
    let element = cloneDeep(textElement);
    if (element.parts===undefined) element.parts = [];
    if (element.parts.length===0) {
        let part: ITextPartAttributes = {length: element.text.length, style: {}};
        element.parts.push(part);
    }
    let parts: ITextPartAttributes[] = [];
    for (let i=0;i<element.parts.length;i++) {
        let newAttributes = element.style ? {...element.style} : {};
        if (element.parts[i].style) {
            removeUndefinedFields(element.parts[i].style);
        }
        if (element.parts[i].style) newAttributes = {...newAttributes, ...element.parts[i].style};
        let part: ITextPartAttributes = {length: element.parts[i].length, style: newAttributes};
        parts.push(part);
    }
    return parts;
}

export const textElementToHtmlString = (textElement: ITextAttributes, partsForRendering?: ITextPartRenderingAttributes[], lineIndexToRender?: number, lineHeight?: number): string => {
    let html: string = "";
    if (lineIndexToRender === undefined) {
        const parts: ITextPartAttributes[] = publishGeneralAttributesToParts(textElement);
        for (let i=0;i<parts.length;i++) {
            html += partToHtmlString(parts[i], textElement, i, lineHeight);
        }
    }
    else {
        const linesInformation: ILineInformation[] = getLinesInformation(partsForRendering!);
        if (lineIndexToRender>=0 && linesInformation.length>lineIndexToRender) {
            for (let i=linesInformation[lineIndexToRender].partStart;i<=linesInformation[lineIndexToRender].partEnd;i++) {
                html += partToHtmlString(partsForRendering![i], textElement, i, lineHeight);
            }
        }
    }

    return html;
}

export const textElementToHtmlTags = (textElement: ITextAttributes): IHtmlTagDefinition[] => {
    const parts: ITextPartAttributes[] = publishGeneralAttributesToParts(textElement);
    let htmlElements: IHtmlTagDefinition[] = [];
    for (let i=0;i<parts.length;i++) {
        const htmlElement = partToHtmlTag(parts[i], textElement, i);
        if (htmlElement) htmlElements.push(htmlElement);
    }
    return htmlElements;
}

const getSelectionPartIndex = (node: Node, offset: number): number => {
    if ((node as HTMLElement).id==="editorTextEditAddIn") return offset;
    if (!node) return 0;
    if ((node as Element).className === "partTextElement") {
        return Array.prototype.indexOf.call(node.parentNode?.children, node);
    }
    else {
        return getSelectionPartIndex(node.parentNode!, offset);
    }
}

const getTextIndex = (partIndex: number, partOffset: number) => {
    const editElement = getHtmlElementFromEditText();
    let textIndex = partOffset;
    for (let i=0;i<partIndex;i++) {
        if ((editElement?.children[i]! as HTMLSpanElement)) {
            textIndex += ((editElement?.children[i]! as HTMLSpanElement).textContent??"").length;
        }
    }
    return textIndex;
}

const getFinalOffset = (node: Node, offset: number): number => {
    if ((node as HTMLElement).id==="editorTextEditAddIn") return 0;
    let finalOffset = offset;
    let previousChild = node;
    while (previousChild.previousSibling) {
        finalOffset += (previousChild.previousSibling.textContent?.length??0);
        previousChild = previousChild.previousSibling;
    }
    return finalOffset;
}

export const getTextSelectionInformation = (): ITextSelectionInformation|undefined => {
    const selection = document.getSelection();
    const baseOffset = getFinalOffset(selection!.anchorNode!, selection!.anchorOffset);
    const baseIndex = getSelectionPartIndex(selection!.anchorNode!, selection!.anchorOffset);
    const endOffset = getFinalOffset(selection!.focusNode!, selection!.focusOffset);
    const endIndex = getSelectionPartIndex(selection!.focusNode!, selection!.focusOffset);
    let selectionStart = getTextIndex(baseIndex,baseOffset);
    let selectionEnd = getTextIndex(endIndex,endOffset);
    if (selectionStart>selectionEnd) {
        const aux = selectionStart;
        selectionStart = selectionEnd;
        selectionEnd = aux;
    }
    const length = selectionEnd - selectionStart;
    const textSelectionInformation: ITextSelectionInformation|undefined = selection===undefined? undefined:{start: selectionStart, length: length};
    return textSelectionInformation;
}

export const getTextCommandScope = (command: string): string[]|undefined => {
    const definition = textCommandDefinitions.map((commandDefinition) => { if(commandDefinition.command===command) return commandDefinition});
    if (definition && definition.length>0) {
        return definition[0]!.scope;
    }
    return undefined;
}

export const getHtmlTextFromEditText = () => {
    return document.getElementById('editorTextEditAddIn')?.innerHTML??""
}

export const getHtmlElementFromEditText = (): HTMLElement|null => {
    return document.getElementById('editorTextEditAddIn')
}

const findLastElement = (htmlPartElement: Element): Element => {
    if (htmlPartElement.children===undefined || htmlPartElement.children.length===0) {
        return htmlPartElement;
    }
    return findLastElement(htmlPartElement.children[0]);
}

export interface IPropertyValue {
    property: string;
    value: string;
}

export interface IHtmlElementDefinition {
    tag: string;
    attributes: IPropertyValue[];
    styles: IPropertyValue[];
}

const getTagsAttributesAndStyles = (htmlElement: HTMLElement): IHtmlElementDefinition|undefined => {
    if (htmlElement.tagName && htmlElement.tagName!=="") {
        const tagName = htmlElement.tagName;
        let attributes: IPropertyValue[] = [];
        for (let i=0;i<htmlElement.attributes.length;i++) {
            if (htmlElement.attributes[i].name.toLowerCase()!=="style") {
                attributes.push({property: htmlElement.attributes[i].name, value: htmlElement.attributes[i].value});
            }
        }
        let styles: IPropertyValue[] = [];
        if (htmlElement.style) {
            for (let i=0;i<htmlElement.style.length;i++) {
                styles.push({property: htmlElement.style[i], value: htmlElement.style.getPropertyValue(htmlElement.style[i])});
            }
        }
        const htmlElementDefinition: IHtmlElementDefinition = {tag: tagName, attributes: attributes, styles: styles};
        return htmlElementDefinition; 
    }
    return undefined;
}

export interface IHtmlElementDefinitionPerPart {
    partIndex: number;
    definitions: IHtmlElementDefinition[];
    textContent: string;
}

const getTagsAttributesAndStylesFromElementAndChildren = (htmlElement: HTMLElement): IHtmlElementDefinitionPerPart[] => {
    let partsDefinition : IHtmlElementDefinitionPerPart[] = [];
    let mainDefinitions: IHtmlElementDefinition[] = [];
    let elementDefinition = getTagsAttributesAndStyles(htmlElement); 
    if (elementDefinition) {
        mainDefinitions.push(elementDefinition);
        if (htmlElement.childNodes && htmlElement.childNodes.length>0) {
            for (let i=0;i<htmlElement.childNodes.length;i++) {
                if (htmlElement.childNodes[i].nodeType===Node.TEXT_NODE) {
                    let definitionsPart = cloneDeep(mainDefinitions);
                    partsDefinition.push({partIndex: 0, definitions: definitionsPart, textContent: htmlElement.childNodes[i].nodeValue??""});
                }
            }
        }
        if (htmlElement.children && htmlElement.children.length>0) {
            for (let i=0;i<htmlElement.children.length;i++) {
                if (htmlElement.children[i].nodeType===Node.ELEMENT_NODE) {
                    let childrenPartsDefinitions: IHtmlElementDefinitionPerPart[] = getTagsAttributesAndStylesFromElementAndChildren(htmlElement.children[i] as HTMLElement);
                    for (let a=0;a<childrenPartsDefinitions.length;a++) {
                        let definitionsPart = cloneDeep(mainDefinitions);
                        definitionsPart = definitionsPart.concat(childrenPartsDefinitions[a].definitions);
                        partsDefinition.push({partIndex: a, definitions: definitionsPart, textContent: childrenPartsDefinitions[a].textContent});
                    }
                }
            }
        }
    }
    return partsDefinition;
}

const getDefinitionValue = (propertyAffected: string, propertyAffectedValue: string|undefined, propertyDefinition: string, propertyValue: string): string|undefined => {
    if (propertyAffected.toLowerCase() === propertyDefinition.toLowerCase()) {
        if (propertyAffectedValue) {
            if (propertyAffectedValue.toLowerCase() === propertyValue.toLowerCase()) {
                return propertyValue;
            }
        }
        else {
            return propertyValue;
        }
    }
    return undefined;
}

const getValue = (htmlDefinitionAttributes: IPropertyValue[], htmlDefinitionStyles: IPropertyValue[], textCommandDefinitionAttributeAffected: string|undefined, textCommandDefinitionStyleAffected: string|undefined, textCommandDefinitionValue: string|undefined) => {
    if (textCommandDefinitionAttributeAffected) {
        for(let i=0;i<htmlDefinitionAttributes.length;i++) {
            const definitionValue = getDefinitionValue(textCommandDefinitionAttributeAffected!, textCommandDefinitionValue, htmlDefinitionAttributes[i].property, htmlDefinitionAttributes[i].value);
            if (definitionValue!==undefined) return definitionValue;
        }
    }
    else if (textCommandDefinitionStyleAffected) {
        for(let i=0;i<htmlDefinitionStyles.length;i++) {
            const definitionValue = getDefinitionValue(textCommandDefinitionStyleAffected!, textCommandDefinitionValue, htmlDefinitionStyles[i].property, htmlDefinitionStyles[i].value);
            if (definitionValue!==undefined) return definitionValue;
        }
    }
    else {
        return "true";
    }
}

const getCommandValue = (htmlDefinition: IHtmlElementDefinition, textCommandDefinition: ITextCommandDefinition): string|undefined => {
    if (htmlDefinition.tag.toLowerCase()===textCommandDefinition.htmlTag.toLowerCase()) {
        const definitionValue = getValue(htmlDefinition.attributes, htmlDefinition.styles, textCommandDefinition.attributeAffected, textCommandDefinition.styleAffected, textCommandDefinition.value);
        if (definitionValue!==undefined) return definitionValue;
    }
    if (textCommandDefinition.aliases && textCommandDefinition.aliases.length>0) {
        for (let i=0;i<textCommandDefinition.aliases.length;i++) {
            if (htmlDefinition.tag.toLowerCase()===textCommandDefinition.aliases[i].htmlTag.toLowerCase()) {
                const definitionValue = getValue(htmlDefinition.attributes, htmlDefinition.styles, textCommandDefinition.aliases[i].attributeAffected, textCommandDefinition.aliases[i].styleAffected, textCommandDefinition.aliases[i].value);
                if (definitionValue!==undefined) return definitionValue;
            }
        }
    }
    return undefined;
}

const checkBooleanValue = (commandValue: string|undefined, textCommandDefinition: ITextCommandDefinition):boolean => {
    if (!commandValue) return false;
    if (commandValue==="true") return true;
    if (textCommandDefinition.value && commandValue.toLowerCase().replaceAll("'","").replaceAll("\"","")===textCommandDefinition.value.replaceAll("'","").replaceAll("\"","")) return true;
    if (textCommandDefinition.aliases && textCommandDefinition.aliases.length>0){
        for (let i=0;i<textCommandDefinition.aliases.length;i++) {
            if (textCommandDefinition.aliases[i].value && commandValue.toLowerCase().replaceAll("'","").replaceAll("\"","")===textCommandDefinition.aliases[i].value?.replaceAll("'","").replaceAll("\"","")) return true;
        }
    }
    return false
}

const getPartsFromHtml = (htmlPartElement: HTMLElement): ITextPartAttributes[] => {
    let parts: ITextPartAttributes[] = [];
    const htmlPartDefinitions: IHtmlElementDefinitionPerPart[] = getTagsAttributesAndStylesFromElementAndChildren(htmlPartElement);

    for (let b=0;b<htmlPartDefinitions.length;b++) {
        const htmlDefinitions: IHtmlElementDefinition[] = htmlPartDefinitions[b].definitions;
        let partStyles: ITextStyle = {};
        const textContent = htmlPartDefinitions[b].textContent;
        for (let i=0;i<htmlDefinitions.length;i++) {
            for (let a=0;a<textCommandDefinitions.length;a++) {
                const commandValue = getCommandValue(htmlDefinitions[i],textCommandDefinitions[a]);
                if (commandValue) {
                    if (textCommandDefinitions[a].command === "fontFamily") {
                        partStyles.fontFamily = commandValue.replaceAll("'","").replaceAll("\"","").replaceAll("font-","");
                    }
                    if (textCommandDefinitions[a].command === "fontSize") {
                        partStyles.fontSize = parseInt(commandValue.replace("px",""));
                    }
                    if (textCommandDefinitions[a].command === "fontItalic") {
                        partStyles.fontItalic = checkBooleanValue(commandValue, textCommandDefinitions[a]);
                    }
                    if (textCommandDefinitions[a].command === "fontUpperCase") {
                        partStyles.fontUpperCase = checkBooleanValue(commandValue, textCommandDefinitions[a])?2:undefined;
                    }
                    if (textCommandDefinitions[a].command === "fontWeight") {
                        partStyles.fontWeight = checkBooleanValue(commandValue, textCommandDefinitions[a]);
                    }
                    if (textCommandDefinitions[a].command === "color") {
                        partStyles.color = commandValue;
                    }
                    if (textCommandDefinitions[a].command === "fontUnderline") {
                        partStyles.fontUnderline = checkBooleanValue(commandValue, textCommandDefinitions[a]);
                    }
                    if (textCommandDefinitions[a].command === "subscript") {
                        partStyles.subscript = true;
                    }
                    if (textCommandDefinitions[a].command === "superscript") {
                        partStyles.superscript = true;
                    }
                    if (textCommandDefinitions[a].command === "lineHeight") {
                        partStyles.lineHeight = parseFloat(commandValue.replace("px",""));
                    }
                    if (textCommandDefinitions[a].command === "letterSpacing") {
                        partStyles.letterSpacing = (parseInt(commandValue.replace("px","")) * 100);
                    }
                }
            }
        }
        let part: ITextPartAttributes = {length: textContent.length, style: partStyles};
        parts.push(part);
    }
    return parts;
}

const removeCeroLengthParts = (parts: ITextPartAttributes[]): ITextPartAttributes[] => {
    let newParts: ITextPartAttributes[] = [];
    for (let i=0;i<parts.length;i++) {
        if (parts[i].length>0) {
            newParts.push({...parts[i]});
        }
    }
    return newParts;
}

const compareStyles = (style1: ITextStyle, style2: ITextStyle) => {
    if ((style1.fontFamily??"")!==(style2.fontFamily??"")) return false;
    if ((style1.fontSize??0)!==(style2.fontSize??0)) return false;
    if ((style1.fontItalic??false)!==(style2.fontItalic??false)) return false;
    if ((style1.fontUpperCase??0)!==(style2.fontUpperCase??0)) return false;
    if ((style1.fontWeight??false)!==(style2.fontWeight??false)) return false;
    if ((style1.color??"")!==(style2.color??"")) return false;
    if ((style1.fontUnderline??false)!==(style2.fontUnderline??false)) return false;
    if ((style1.subscript??false)!==(style2.subscript??false)) return false;
    if ((style1.superscript??false)!==(style2.superscript??false)) return false;
    if ((style1.lineHeight??false)!==(style2.lineHeight??false)) return false;
    if ((style1.letterSpacing??false)!==(style2.letterSpacing??false)) return false;
    return true;
}

const unionSameParts = (parts: ITextPartAttributes[]): ITextPartAttributes[] => {
    let newParts: ITextPartAttributes[] = [];
    let position: number = 1;
    newParts.push({...parts[0]});
    while (position<parts.length) {
        const equals = compareStyles(newParts[newParts.length-1].style,parts[position].style);
        if (equals) {
            newParts[newParts.length-1].length += parts[position].length;
        }
        else {
            newParts.push({...parts[position]});
        }
        position++;
    }
    return newParts;
}

const cleanParts = (parts: ITextPartAttributes[]): ITextPartAttributes[] => {
    let newParts: ITextPartAttributes[] = cloneDeep(parts);
    newParts = removeCeroLengthParts(newParts);
    newParts = unionSameParts(newParts);
    return newParts; 
}

const replaceTextContentAtHtml = (htmlElement: HTMLElement, newTextContent: string) => {
    if (htmlElement.childNodes && htmlElement.childNodes.length>0) {
        for(let i=0;i<htmlElement.childNodes.length;i++) {
            if (htmlElement.childNodes[i] !== undefined) {
                if (htmlElement.childNodes[i].nodeType === Node.TEXT_NODE) {
                    htmlElement.childNodes[i].nodeValue = newTextContent;
                }
                else {
                    if ((htmlElement.childNodes[i] as HTMLElement)!==undefined) {
                        if ((htmlElement.childNodes[i] as HTMLElement)!==undefined) {
                            replaceTextContentAtHtml(htmlElement.childNodes[i] as HTMLElement, newTextContent);
                        }
                    }
                }
            }
        }
    }
}

const getPartsFromHtmlEditElement = (htmlEditElement: HTMLElement): ITextPartAttributes[] => {
    let parts: ITextPartAttributes[] = [];
    for(let i=0;i<htmlEditElement.children.length;i++) {
        if ((htmlEditElement.children[i] as HTMLElement)!==undefined) {
            if ((htmlEditElement.children[i] as HTMLElement)!==undefined) {
                const newparts = getPartsFromHtml((htmlEditElement.children[i] as HTMLElement));
                if (newparts && newparts.length>0) {
                    for (let i=0;i<newparts.length;i++) {
                        parts.push(newparts[i]);
                    }
                }
            }
        }
    }
    return parts;
}

const partsToTextElementAttributes = (lastAttributes: ITextAttributes, text: string, parts: ITextPartAttributes[]): ITextAttributes => {
    let generalStyles: ITextStyle = {}
    let sameFontFamily: boolean = true;
    let sameFontSize: boolean = true;
    let sameFontItalic: boolean = true;
    let sameFontUpperCase: boolean = true;
    let sameFontWeight: boolean = true;
    let sameColor: boolean = true;
    let sameFontUnderline: boolean = true;
    let sameSubscript: boolean = true;
    let sameSuperscript: boolean = true;
    let sameLineHeight: boolean = true;
    let sameLetterSpacing: boolean = true;
    for (let i=1;i<parts.length;i++) {
        if (parts[i].style.fontFamily!==parts[0].style.fontFamily) sameFontFamily = false;
        if (parts[i].style.fontSize!==parts[0].style.fontSize) sameFontSize = false;
        if (parts[i].style.fontItalic!==parts[0].style.fontItalic) sameFontItalic = false;
        if (parts[i].style.fontUpperCase!==parts[0].style.fontUpperCase) sameFontUpperCase = false;
        if (parts[i].style.fontWeight!==parts[0].style.fontWeight) sameFontWeight = false;
        if (parts[i].style.color!==parts[0].style.color) sameColor = false;
        if (parts[i].style.fontUnderline!==parts[0].style.fontUnderline) sameFontUnderline = false;
        if (parts[i].style.subscript!==parts[0].style.subscript) sameSubscript = false;
        if (parts[i].style.superscript!==parts[0].style.superscript) sameSuperscript = false;
        if (parts[i].style.lineHeight!==parts[0].style.lineHeight) sameLineHeight = false;
        if (parts[i].style.letterSpacing!==parts[0].style.letterSpacing) sameLetterSpacing = false;
    }
    if (parts.length>0) {
        if (sameFontFamily) {
            generalStyles.fontFamily = parts[0].style.fontFamily;
            for (let i=0;i<parts.length;i++) parts[i].style.fontFamily = undefined;
        }
        if (sameFontSize) {
            generalStyles.fontSize = parts[0].style.fontSize;
            for (let i=0;i<parts.length;i++) parts[i].style.fontSize = undefined;
        }
        if (sameFontItalic) {
            generalStyles.fontItalic = parts[0].style.fontItalic;
            for (let i=0;i<parts.length;i++) parts[i].style.fontItalic = undefined;
        }
        if (sameFontUpperCase) {
            generalStyles.fontUpperCase = parts[0].style.fontUpperCase;
            for (let i=0;i<parts.length;i++) parts[i].style.fontUpperCase = undefined;
        }
        if (sameFontWeight) {
            generalStyles.fontWeight = parts[0].style.fontWeight;
            for (let i=0;i<parts.length;i++) parts[i].style.fontWeight = undefined;
        }
        if (sameColor) {
            generalStyles.color = parts[0].style.color;
            for (let i=0;i<parts.length;i++) parts[i].style.color = undefined;
        }
        if (sameFontUnderline) {
            generalStyles.fontUnderline = parts[0].style.fontUnderline;
            for (let i=0;i<parts.length;i++) parts[i].style.fontUnderline = undefined;
        }
        if (sameSubscript) {
            generalStyles.subscript = parts[0].style.subscript;
            for (let i=0;i<parts.length;i++) parts[i].style.subscript = undefined;
        }
        if (sameSuperscript) {
            generalStyles.superscript = parts[0].style.superscript;
            for (let i=0;i<parts.length;i++) parts[i].style.superscript = undefined;
        }
        if (sameLineHeight) {
            generalStyles.lineHeight = parts[0].style.lineHeight;
            for (let i=0;i<parts.length;i++) parts[i].style.lineHeight = undefined;
        }
        if (sameLetterSpacing) {
            generalStyles.letterSpacing = parts[0].style.letterSpacing;
            for (let i=0;i<parts.length;i++) parts[i].style.letterSpacing = undefined;
        }
    }

    let textAlign: string|undefined = lastAttributes.textAlign;

    parts = cleanParts(parts);

    let textElement: ITextAttributes = {...lastAttributes, text: text, textAlign: textAlign, style: generalStyles, parts: parts};

    return textElement;
}

export const htmlTagsToTextElement = (lastAttributes: ITextAttributes, text: string, cursorposition: number|undefined): ITextAttributes|undefined => {
    const editElement = getHtmlElementFromEditText();
    if (editElement===null) {
        return undefined;
    }
    let parts: ITextPartAttributes[] = getPartsFromHtmlEditElement(editElement);

    const textElement = partsToTextElementAttributes(lastAttributes, text, parts);
    if (textElement.text === "" && (!textElement.style || !textElement.style.fontSize)) {
        if (!textElement.style) {
            if (lastAttributes.parts.length>0 && lastAttributes.parts[0].style !== undefined) textElement.style = {...lastAttributes.parts[0].style};
            else textElement.style = {};
        }
        if (!textElement.style.fontSize) textElement.style.fontSize = 14;
        textElement.text = " ";
        if (!textElement.parts || textElement.parts.length === 0) {
            if (lastAttributes.parts.length>0 && lastAttributes.parts[0].style !== undefined) textElement.parts.push({length:1, style:{...lastAttributes.parts[0].style}});
            else textElement.parts.push({length:1, style:{fontSize: 14}});
        }
        else {
            if (lastAttributes.parts.length>0 && lastAttributes.parts[0].style !== undefined) textElement.parts[0] = {...textElement.parts[0], length:1, style:{...lastAttributes.parts[0].style}};
            else textElement.parts[0] = {...textElement.parts[0], length:1, style:{...textElement.parts[0].style, fontSize: 14}}; 
        }
    }

    if (editElement && cursorposition!==undefined) restoreSelection(editElement, cursorposition, 0);
    return textElement;
}

export const intersec = (startIndexR1: number, endIndexR1: number, startIndexR2: number, endIndexR2: number): boolean => {
    if (endIndexR1<startIndexR1 || endIndexR2<startIndexR2) return false;
    if((startIndexR2>=startIndexR1 && startIndexR2<=endIndexR1) || (endIndexR2>=startIndexR1 && endIndexR2<=endIndexR1)){
        return true;
    }
    if((startIndexR1>=startIndexR2 && startIndexR1<=endIndexR2) || (endIndexR1>=startIndexR2 && endIndexR1<=endIndexR2)){
        return true;
    }
    if((startIndexR1<startIndexR2 && endIndexR1>endIndexR2) || (startIndexR2<startIndexR1 && endIndexR2>endIndexR1)){
        return true;
    }
    return false;
} 

export const createNewPartsForSelection = (textSelectionInformation: ITextSelectionInformation): ITextPartAttributes[] => {
    const selectionStart = textSelectionInformation.start;
    let selectionEnd = textSelectionInformation.start + textSelectionInformation.length -1;
    const editElement = getHtmlElementFromEditText();
    if (editElement===null) {
        return [];
    }
    const parts: ITextPartAttributes[] = getPartsFromHtmlEditElement(editElement);
    let newParts: ITextPartAttributes[] = [];
    let totalCount = 0;
    for (let i=0;i<parts.length;i++) {
        const partStart = totalCount;
        const partEnd = totalCount + parts[i].length -1;
        const inPart = intersec(selectionStart, selectionEnd, partStart, partEnd);
        if (inPart) {
            let divide: boolean = true;
            let startDivision: number|undefined = undefined;
            let endDivision: number|undefined = undefined;

            if (partStart===selectionStart && partEnd===selectionEnd) {
                return parts;
            }
            if (selectionStart<=partStart && selectionEnd>=partEnd) {
                newParts.push(parts[i]);
            }
            else {
                if (selectionStart>partStart) {
                    let partAdded1: ITextPartAttributes = cloneDeep(parts[i]);
                    partAdded1.length = selectionStart-partStart;
                    newParts.push(partAdded1);
                }
                else {
                    let partAdded1: ITextPartAttributes = cloneDeep(parts[i]);
                    partAdded1.length = selectionEnd-partStart+1;
                    newParts.push(partAdded1);
                }
                if (selectionStart>partStart && selectionEnd<partEnd) {
                    let partAdded2: ITextPartAttributes = cloneDeep(parts[i]);
                    partAdded2.length = selectionEnd-selectionStart+1;
                    newParts.push(partAdded2);
                }
                if (selectionEnd<partEnd) {
                    let partAdded3: ITextPartAttributes = cloneDeep(parts[i]);
                    partAdded3.length = partEnd-selectionEnd;
                    newParts.push(partAdded3);
                }
                else {
                    let partAdded3: ITextPartAttributes = cloneDeep(parts[i]);
                    partAdded3.length = partEnd-selectionStart+1;
                    newParts.push(partAdded3);
                }
            }
        }
        else {
            newParts.push(parts[i]);
        }
        totalCount+=parts[i].length;
    }
    return newParts;
}

export const breakPartsForLineBreak = (indexBreak: number, parts: ITextPartAttributes[]): ITextPartAttributes[] => {
    let newParts: ITextPartAttributes[] = []; 
    let totalCount = 0;
    let inserted = false;
    for (let i=0;i<parts.length;i++) {
        let start = totalCount;
        totalCount+=parts[i].length;
        let end = totalCount;
        if (start === indexBreak) {
            return parts;
        }
        else if (start < indexBreak && end > indexBreak && !inserted) {
            const secondPartLength = end-indexBreak;
            const firstPartLength = parts[i].length - secondPartLength;
            newParts.push({...parts[i], length: firstPartLength});
            newParts.push({...parts[i], length: secondPartLength});
            inserted = true;
        }
        else {
            newParts.push({...parts[i]});
        }
    }

    return newParts;
}

const findPartsIndexToChange = (parts: ITextPartAttributes[], textSelectionInformation?: ITextSelectionInformation): number[] => {
    let indexes: number[] = [];
    let totalCount: number = 0;
    for(let i=0;i<parts.length;i++) {
        if(!textSelectionInformation || (totalCount>=textSelectionInformation.start && totalCount<textSelectionInformation.start+textSelectionInformation.length)) {
            indexes.push(i);
        }
        totalCount += parts[i].length;
    }
    return indexes;
}

const applyFontSize = (style: any, value: IFontSizeValue): any => {
    let newSize = 0;
    let oldSize = 0;
    if (style && style["fontSize"]) {
        oldSize = style["fontSize"];
    }
    if (value.fontSize) {
        newSize = value.fontSize;    
    }
    else if(value.increease) {
        newSize = oldSize + 1;    
    }
    else {
        newSize = oldSize - 1;    
    }
    if (newSize!==0) {
        style["fontSize"] = newSize;
    }
    return style;
}

export const execCommandToSelectedText = (command: string, lastAttributes: ITextAttributes, textSelectionInformation?: ITextSelectionInformation, value?: any, restoreTextSelection?: boolean): ITextAttributes => {
    let parts: ITextPartAttributes[] = [];
    const editElement = getHtmlElementFromEditText();
    let text: string = (textSelectionInformation?editElement!.textContent??"":lastAttributes.text);
    if (textSelectionInformation===undefined) {
        parts = publishGeneralAttributesToParts(lastAttributes);
    }
    else {
        parts = createNewPartsForSelection(textSelectionInformation);
    }

    const partsIndexToChange: number[] = findPartsIndexToChange(parts, textSelectionInformation);

    for(let i=partsIndexToChange.length-1;i>=0;i--) {
        if (!parts[partsIndexToChange[i]].style) {
            parts[partsIndexToChange[i]].style={};
        }
        let style: any = {...parts[partsIndexToChange[i]].style};
        if (command==="fontSize") style = applyFontSize(style, value);
        else style[command] = value; 
        parts[partsIndexToChange[i]].style = style;
    }

    let element = partsToTextElementAttributes(lastAttributes, text, parts);

    const otherInputsElements: string[] = ["size"];

    if (editElement) { 
        const html = getHtmlFromTexElement(element);
        editElement!.innerHTML = html;
        const isInOtherRestrictedInputs: boolean = (document.activeElement?otherInputsElements.includes(document.activeElement.id):false);
        if (textSelectionInformation && !isInOtherRestrictedInputs) {
            let length = textSelectionInformation.length;
            if (restoreTextSelection !== false) {
                restoreSelection(editElement, textSelectionInformation!.start, length);
            }
        } 
    }

    return element;
}

export const getSelectionStyleValue = (element: ITextAttributes, styleProperty: string, textSelectionInformation: ITextSelectionInformation): any => {
    let value = getStyleValue(element.style, styleProperty);
    if (value!==undefined) return value;
    let partStart = 0;
    const selectionStart = textSelectionInformation.start;
    let selectionEnd = textSelectionInformation.start + textSelectionInformation.length - 1;
    if (selectionEnd < selectionStart) selectionEnd = selectionStart;
    for(let i=0;i<element.parts.length;i++) {
        const partEnd = partStart + element.parts[i].length - 1;
        const inPart = intersec(selectionStart, selectionEnd, partStart, partEnd);
        if (inPart){
            if (value===undefined) {
                value = getStyleValue(element.parts[i].style, styleProperty);
            }
            else {
                if (value != getStyleValue(element.parts[i].style, styleProperty)) return undefined;
            }
        }
        partStart += element.parts[i].length;
    }
    return value;
}

export const getStyleValue = (style: ITextStyle, styleProperty: string): any => {
    if (styleProperty==="fontFamily") return style.fontFamily;
    if (styleProperty==="fontSize") return style.fontSize;
    if (styleProperty==="fontItalic") return style.fontItalic;
    if (styleProperty==="fontUpperCase") return style.fontUpperCase;
    if (styleProperty==="fontWeight") return style.fontWeight;
    if (styleProperty==="color") return style.color;
    if (styleProperty==="fontUnderline") return style.fontUnderline;
    if (styleProperty==="subscript") return style.subscript;
    if (styleProperty==="superscript") return style.superscript;
    if (styleProperty==="letterSpacing") return style.letterSpacing;
    if (styleProperty==="lineHeight") return style.lineHeight;
    return undefined;
}

export const getHtmlFromTexElement = (element: ITextAttributes): string => {
    const html = textElementToHtmlString(element);
    return html;
}

export const getPartIndex = (parts: ITextPartAttributes[], position: number): number => {
    let totalCount = 0;
    for (let i=0;i<parts.length;i++) {
        totalCount+=parts[i].length;
        if (totalCount>position) {
            return i;
        }
    }
    return parts.length-1;
}

export interface INodeAndOffset {
    node: Node;
    offset: number;
}

const getNodeAndOffset = (element: HTMLElement, position: any): INodeAndOffset|undefined => {
    if (position.value === 0) {
        if (!element.children || element.children.length === 0) {
            const nodeAndOffset = {node: element, offset: 0};
            return nodeAndOffset;
        }
        else {
            return getNodeAndOffset(element.children[0] as HTMLElement,position);
        }
    }
    else {
        for (let i=0;i<element.childNodes.length;i++) {
            if (element.childNodes[i].nodeType === Node.TEXT_NODE) {
                if (element.childNodes[i].nodeValue!.length >= position.value) {
                    const nodeAndOffset = {node: element.childNodes[i], offset: position.value};
                    return nodeAndOffset;
                }
                else {
                    position.value = position.value - element.childNodes[i].nodeValue!.length;
                }
            }
            else {
                const nodeAndOffset = getNodeAndOffset (element.childNodes[i] as HTMLElement, position);
                if(nodeAndOffset) return nodeAndOffset;
            }
        }
    }
    return undefined;
}

const addGeneralAttributesToHtmlString = (textElement: ITextAttributes, html: string): string => {
    let htmlTagString: IHtmlTagString = {openTag: "<div>", closeTag: "</div>"};
    if (textElement.textAlign) htmlTagString = insertHtmlTagString(htmlTagString, execCommandToHtmlString("textAlign", textElement.textAlign.toString()));

    html = htmlTagString.openTag + html + htmlTagString.closeTag;
    return html;
} 

const findLettersPositionAndTotalHeight = (html: string, lettersParts: ITextPartAttributes[], element: ITextAttributes): IPositionsAndTotalHeight => {
    let minMove: number|undefined = undefined;
    html=addGeneralAttributesToHtmlString(element, html);
    let lettersPosition: IPoint[] = [];
    let totalHeight = 0;
    var div = document.createElement('div');
    try {
        div.style["whiteSpace"] = "pre-line";
        div.style["display"] = "inline-block";
        div.style["wordBreak"] = "break-word";
        if(element.width) {
            div.style["width"] = element.width!.toString() + "px";
        }
        div.style["fontSize"] = "0px";
        if (html.includes("lineHeight") || html.includes("line-height")) {
            div.style["lineHeight"] = "0";
        }
        div.innerHTML = html;
        document.body.appendChild(div);
        totalHeight = div.offsetHeight;
        const letterHtmlElements = div.getElementsByClassName("partTextElement");
        for (let i=0;i<letterHtmlElements.length;i++) {
            if (letterHtmlElements[i].textContent!="") {
                var parentPos = ((letterHtmlElements[i] as HTMLSpanElement).parentNode as HTMLElement).getBoundingClientRect();
                const rect = (letterHtmlElements[i] as HTMLSpanElement).getBoundingClientRect();
                const y = (rect.y - parentPos.y)
                if (minMove === undefined || y < minMove) {
                    minMove = y;
                }
                const position = {x: rect.x, y: y};
                lettersPosition.push(position);
            }
        }
        if (minMove) {
            for (let i=0;i<lettersPosition.length;i++) {
                lettersPosition[i].y = lettersPosition[i].y - minMove;
            }
        }
    } finally {
        div.parentNode?.removeChild(div);
    }
    const pointsAndHeight: IPositionsAndTotalHeight = {points: lettersPosition, totalHeight: totalHeight};
    return pointsAndHeight;
}

const getPartBreaks = (partIndex: number, element: ITextAttributes, lettersPosition: IPoint[]): ITextPartAttributes[] => {
    let partBreaks: ITextPartAttributes[] = [];
    let start = 0;
    let inserted = 0;
    for (let i=0;i<partIndex;i++) {
        start += element.parts[i].length;
    }
    for (let i=1;i<element.parts[partIndex].length;i++) {
        if (lettersPosition[start+i].y>lettersPosition[start+i-1].y) {
            partBreaks.push({length:(i-inserted), style: element.parts[partIndex].style});
            inserted += (i-inserted);
        }
    }
    if (element.parts[partIndex].length-inserted>0) {
        partBreaks.push({length:(element.parts[partIndex].length-inserted), style: element.parts[partIndex].style});
    }
    return partBreaks;
}

export interface ILineInformation {
    partStart: number;
    partEnd: number;
    height: number;
    baseline: number;
}

export const getLinesInformation = (partsForRendering: ITextPartRenderingAttributes[]): ILineInformation[] => {
    let linesInformation: ILineInformation[] = [];

    if (partsForRendering && partsForRendering.length>0) {
        let partStart = 0, baseline = partsForRendering[0].baseline, height = partsForRendering[0].height;
        for (let i=1;i<partsForRendering.length;i++) {
            if (partsForRendering[i].y>partsForRendering[i-1].y) {
                linesInformation.push({partStart: partStart, partEnd: i-1, height: height, baseline: baseline});
                partStart = i;
                baseline = partsForRendering[i].baseline;
                height = partsForRendering[i].height;
            }
            else {
                if (baseline < partsForRendering[i].baseline) {
                    baseline = partsForRendering[i].baseline;
                } 
                if (height < partsForRendering[i].height) {
                    height = partsForRendering[i].height;
                } 
            }
        }
        linesInformation.push({partStart: partStart, partEnd: partsForRendering.length-1, height: height, baseline: baseline});
    }
    return linesInformation;
}

export const pixelsLetterSpace = 0.76;

const updatePartsYPosition = (partsForRendering: ITextPartRenderingAttributes[]): ITextPartRenderingAttributes[] => {
    if (partsForRendering && partsForRendering.length>0) {
        const linesInformation: ILineInformation[] = getLinesInformation(partsForRendering);
        let baseLineMove = linesInformation[0].baseline;

        for (let i=0;i<partsForRendering.length;i++) {
            const superScript = partsForRendering[i].style.superscript?(1-superSupFactor):1;
            partsForRendering[i].y += (baseLineMove*pixelsLetterSpace) * superScript;
        }
    }
    return partsForRendering;
}

export const getHtmlElementHeight = (html: string, width?: number): number => {
    let height = 0;
    var div = document.createElement('div');
    try {
        div.style["whiteSpace"] = "pre-line";
        div.style["display"] = "inline-block";
        div.style["wordBreak"] = "break-word";
        if (width!==undefined) div.style["width"] = width.toString() + "px";
        div.style["fontSize"] = "0px";
        if (html.includes("lineHeight") || html.includes("line-height")) {
            div.style["lineHeight"] = "0";
        }
        div.innerHTML = html;
        document.body.appendChild(div);
        height = div.offsetHeight;
    } finally {
        div.parentNode?.removeChild(div);
    }
    return height;
}

export const getTextElementUnasignedWidth = (elementAttributes: ITextAttributes): number => {
    let html = textElementToHtmlString(elementAttributes);
    const width = getHtmlElementWidth(html);
    return width;
}

export const getHtmlElementWidth = (html: string): number => {
    let width = 0;
    var span = document.createElement('span');
    try {
        span.style["whiteSpace"] = "pre-line";
        span.style["display"] = "inline-block";
        span.style["wordBreak"] = "break-word";
        span.style["fontSize"] = "0px";
        if (html.includes("lineHeight") || html.includes("line-height")) {
            span.style["lineHeight"] = "0";
        }
        span.innerHTML = html;
        document.body.appendChild(span);
        width = span.offsetWidth + 2;
    } finally {
        span.parentNode?.removeChild(span);
    }
    return width;
}

const lineHeightForMove = 0.78;

export const getFirstLineHeightMove = (elementAttributes: ITextAttributes, partsForRendering: ITextPartRenderingAttributes[]): number => {
    let html1 = textElementToHtmlString(elementAttributes, partsForRendering, 0, lineHeightForMove);
    let html2 = textElementToHtmlString(elementAttributes, partsForRendering, 0);
    const height1 = getHtmlElementHeight(html1);
    const height2 = getHtmlElementHeight(html2);
    const firstLineMove = (height2 - height1)/2;
    return firstLineMove;
}

export const getLastLineHeightMove = (elementAttributes: ITextAttributes, partsForRendering: ITextPartRenderingAttributes[]): number => {
    const linesInformation: ILineInformation[] = getLinesInformation(partsForRendering);
    let html1 = textElementToHtmlString(elementAttributes, partsForRendering, linesInformation.length-1, lineHeightForMove);
    let html2 = textElementToHtmlString(elementAttributes, partsForRendering, linesInformation.length-1);
    const height1 = getHtmlElementHeight(html1);
    const height2 = getHtmlElementHeight(html2);
    const lastLineMove = (height2 - height1)/2;
    return lastLineMove;
}

export const joinBreakLines = (text: string, parts: ITextPartAttributes[]): ITextPartAttributes[] => {
    let newParts: ITextPartAttributes[] = [];
    let joinedInitialPart = false;
    for (let i=parts.length-1;i>0;i--) {
        const textPart = getTextPart(parts, text, i);
        const textPartBefore = getTextPart(parts, text, i-1);
        newParts = [{...parts[i]}, ...newParts];
        if (!textPart.startsWith("\n") && textPartBefore === "\n") {
            newParts[0].length = newParts[0].length + 1;
            i--;
            if (i===0) joinedInitialPart = true;
        }
    }
    if (!joinedInitialPart) {
        newParts = [{...parts[0]}, ...newParts];
    }
    return newParts; 
}

export const heightOnEmpty = 10;

export const updateRenderingTextSegments = (elementAtributes: ITextAttributes): ITextAttributes => {
    elementAtributes.parts = joinBreakLines(elementAtributes.text, elementAtributes.parts);
    const lettersParts: ITextPartAttributes[] = [];
    for (let i=0;i<elementAtributes.text.length;i++) {
        const partIndex = getPartIndex(elementAtributes.parts, i);
        lettersParts.push({...elementAtributes.parts[partIndex], length: 1});
    }
    let lettersCount = 0;
    for (let i=0;i<elementAtributes.parts.length;i++) {
        lettersCount += elementAtributes.parts[i].length;
    }
    const lettersElement: ITextAttributes = {...elementAtributes, parts: lettersParts};
    const html = textElementToHtmlString(lettersElement);
    const lettersPositionAndTotalHeight = findLettersPositionAndTotalHeight(html, lettersParts, elementAtributes);
    const lettersPosition = lettersPositionAndTotalHeight.points;
    let partsForRendering: ITextPartRenderingAttributes[] = [];
    lettersCount = 0;
    let partForRenderingCount = 0;
    elementAtributes.parts = publishGeneralAttributesToParts(elementAtributes);
    const partBreaksTotal: ITextPartAttributes[] = [];
    for (let i=0;i< elementAtributes.parts.length;i++) {
        const partBreaks: ITextPartAttributes[] = getPartBreaks(i, elementAtributes, lettersPosition);
        for (let i=0;i<partBreaks.length;i++) {
            partBreaksTotal.push({...partBreaks[i]});
            const text: string = getTextPart(partBreaksTotal, elementAtributes.text, partForRenderingCount);
            const measure = measureText(" ", partBreaks[i].style.fontSize, "font-" + partBreaks[i].style.fontFamily);
            if (partBreaks[i].length>0) {
                partsForRendering.push({length: partBreaks[i].length, style: partBreaks[i].style, x: lettersPosition[lettersCount].x, y: lettersPosition[lettersCount].y, text: text, height: measure.height, baseline: measure.ascent});
                lettersCount += partBreaks[i].length;
            }
            partForRenderingCount ++;
        }
    }

    partsForRendering = updatePartsYPosition(partsForRendering);
    elementAtributes.partsForRendering = partsForRendering;
    const firstLineMove = getFirstLineHeightMove(elementAtributes, elementAtributes.partsForRendering);
    const lastLineMove = getLastLineHeightMove(elementAtributes, elementAtributes.partsForRendering);
    let totalHeight = lettersPositionAndTotalHeight.totalHeight - lastLineMove - firstLineMove;
    if (totalHeight<1) totalHeight = elementAtributes.style.fontSize??heightOnEmpty; 
    elementAtributes.height = totalHeight;
    return elementAtributes;
}

function lastNode(element: Node): Node {
    if (element.childNodes && element.childNodes.length>0) return lastNode(element.childNodes[0]);
    else return element;
}

export function restoreSelection(editElement: HTMLElement, startSelection: number, length: number) {
    const endSelection = startSelection + length;
    const startNodeOffset = getNodeAndOffset(editElement, {value: startSelection});
    const endNodeOffset = getNodeAndOffset(editElement, {value: endSelection});
    if (startNodeOffset && endNodeOffset) {
        let range = new Range();
        range.setStart(startNodeOffset.node, startNodeOffset.offset);
        range.setEnd(endNodeOffset.node, endNodeOffset.offset);
        var sel = window.getSelection();
        if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
}

export const isTextPartCommand = (command: string): boolean => {
    for (let i=0;i<textCommandDefinitions.length;i++) {
        if (textCommandDefinitions[i].scope.includes("SELECTION") && textCommandDefinitions[i].command === command) return true;
    }
    return false;
}

export const isTextOnlyGeneralCommand = (command: string): boolean => {
    for (let i=0;i<textCommandDefinitions.length;i++) {
        if (textCommandDefinitions[i].scope.includes("ALL") && !textCommandDefinitions[i].scope.includes("SELECTION") && textCommandDefinitions[i].command === command) return true;
    }
    return false;
}

export const removeSelection = (attributes: ITextAttributes, startSelection: number, length: number): ITextAttributes => {
    const endSelection = startSelection+length;
    let newAttributes = cloneDeep(attributes);
    if (length>0) {
        newAttributes.text = attributes.text.substring(0,startSelection) + attributes.text.substring(endSelection);
        newAttributes.parts = [];
        let total = 0;
        for (let i=0; i<attributes.parts.length; i++) {
            const initPart = total;
            const endPart = initPart + attributes.parts[i].length;
            if (!(initPart>=startSelection && endPart<=endSelection)) {
                newAttributes.parts.push({...attributes.parts[i]});
                const originalLength = newAttributes.parts[newAttributes.parts.length-1].length;
                let partSelectionStart = initPart;
                let partSelectionEnd = endPart;
                let partSelectionLength = 0;
                if ((startSelection>initPart && startSelection<endPart) || (endSelection<endPart && endSelection>initPart)) {
                    if (startSelection>initPart && startSelection<endPart){
                        partSelectionStart = startSelection;
                    }
                    if (endSelection<endPart && endSelection>initPart){
                        partSelectionEnd = endSelection;
                    }
                    partSelectionLength = partSelectionEnd - partSelectionStart;
                }
                newAttributes.parts[newAttributes.parts.length-1].length = (originalLength-partSelectionLength); 
            }
            total += attributes.parts[i].length;
        }
    }
    return newAttributes;
}